// Icons
import {
  Place,
  CalendarToday,
  AccessTime,
  EditCalendar,
  DeleteOutline,
} from "@mui/icons-material";

// Styles
import { styles } from "./styles";

// Libraries
import { useTranslation } from "react-i18next";

// Components
import {
  getLocalizedTime,
  getLocalizedDate,
  getLocalizedStatus,
} from "Components/Language/Common";
import { InfoRow, MNLink } from "Common/Components";
import PanAmNote from "Components/Appointments/Common/PanAmNote";
import { getAppointmentStatusConfig } from "./statusConfig";
import { Card, Typography, Box, Stack, Button, useTheme } from "@mui/material";

// Constants
import { APPOINTMENT_STATUS } from "Constants/AppointmentConstants";

const AppointmentCard = ({
  appointmentSlot,
  openDeleteModal,
  openEditModal,
  isDrawer = false,
  canModify = true,
  actionMessage = "",
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const STATUS_CONFIG = getAppointmentStatusConfig(theme);

  const normalizedStatus =
    typeof appointmentSlot.status === "string"
      ? appointmentSlot.status.toUpperCase()
      : APPOINTMENT_STATUS.Unknown;
  const currentStatusConfig =
    STATUS_CONFIG[normalizedStatus] ||
    STATUS_CONFIG[APPOINTMENT_STATUS.Unknown];
  const { Icon: StatusIcon, colour, showActions } = currentStatusConfig;

  const showDetails = showActions && canModify;
  const showActionButtons = showDetails && !actionMessage;
  const showActionMessage = showDetails && actionMessage;

  return (
    <Card sx={styles.appointmentCard(colour)}>
      <Stack sx={styles.appointmentStack}>
        <Stack
          spacing={1}
          sx={styles.detailsStack}
          direction={isDrawer ? "column" : { xs: "column", md: "row" }}
        >
          <Stack sx={styles.infoStack}>
            <Box key={appointmentSlot.id} sx={styles.statusButton(colour)}>
              <StatusIcon />
              <Typography
                sx={{ fontSize: "1rem", fontWeight: "medium", color: colour }}
              >
                {getLocalizedStatus(normalizedStatus, t)}
              </Typography>
            </Box>
            <Typography sx={styles.clinicTitle}>
              {t("clinic.name", { clinic: appointmentSlot.Clinic })}
            </Typography>
            <InfoRow
              IconComponent={Place}
              statusColour={colour}
              text={t("clinic.address", { clinic: appointmentSlot.Clinic })}
            />
            <InfoRow
              IconComponent={CalendarToday}
              statusColour={colour}
              text={getLocalizedDate(
                appointmentSlot.date,
                "cccc, MMMM dd, yyyy"
              )}
            />
            <InfoRow
              IconComponent={AccessTime}
              statusColour={colour}
              text={getLocalizedTime(appointmentSlot.startTime, "hh:mm a")}
            />
            {appointmentSlot.Clinic?.hoursOfOperationUrl && showDetails && (
              <MNLink
                title={t("clinic.detailsURL")}
                link={appointmentSlot.Clinic.hoursOfOperationUrl}
              />
            )}
          </Stack>
          {showActionButtons && (
            <Stack direction="row" spacing={1} sx={styles.actionStack}>
              <Button
                sx={{ width: { xs: "100%", sm: "fit-content" } }}
                variant="contained"
                startIcon={<EditCalendar />}
                onClick={() => openEditModal(appointmentSlot)}
              >
                {t("upcomingpastappointments.reschedule")}
              </Button>
              <Button
                sx={{ width: { xs: "100%", sm: "fit-content" } }}
                variant="outlined"
                color="error"
                startIcon={<DeleteOutline />}
                onClick={() => openDeleteModal(appointmentSlot)}
              >
                {t("upcomingpastappointments.cancel")}
              </Button>
            </Stack>
          )}
          {showActionMessage && (
            <Typography variant="body2" color="text.secondary">
              {actionMessage}
            </Typography>
          )}
        </Stack>
        {showDetails && (
          <PanAmNote clinicId={appointmentSlot.clinicId} isMBS={isDrawer} />
        )}
      </Stack>
    </Card>
  );
};

export default AppointmentCard;
